import React from "react";
import { Compass, Hash, PenLine, Rss } from "lucide-react";
import { DiscussionWithoutContentDTO } from "@/generated-client";
import { Button } from "@/components/ui/button";
import { ClientFactory } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";
import { buildSharedComposerSeed, writeSharedComposerSeed } from "@/main/composer/sharedComposerSeed";
import { triggerCreateDiscussionEntry } from "@/main/react/create/createDiscussionEntry";
import { Localizer } from "@/main/react/Types";
import { createReactMount } from "@/main/react/mountContract";
import SearchPaginatedList, { seedSearchPaginatedListCache } from "@/main/react/page/search/SearchPaginatedList";
import SearchDiscussionResultRow from "@/main/react/page/search/result-row/SearchDiscussionResultRow";
import { extractSearchResultId } from "@/main/react/page/SearchResultsPage.helpers";
import { crawlableDiscussionListFallback } from "@/main/react/list/ssrFallbacks";
import { logApiFailure } from "@/main/common/ApiUtils";
import { DiscussionLanguageScopeSelect } from "@/main/react/page/profile-page/DiscussionLanguageScopeSelect";
// `import type`, deliberately: `ProfilePageQueries` pulls react-query, and this is a two-value string
// union. A value import here would drag that module into the tag hub's chunk for a type.
import type { UserDiscussionLanguageScope } from "@/main/react/page/profile-page/ProfilePageQueries";
import { useHistoryBackedState } from "@/main/react/page/useHistoryBackedState";

/**
 * The empty card's two action treatments (round 32 A3). One constant each so the lead and the
 * secondary cannot drift apart in the `DISCOVERY-35` / `ERRORS-17` coarse-pointer floor or the
 * `DISCOVERY-44` stacking widths, which both must carry.
 */
const EMPTY_PRIMARY_ACTION_CLASS =
    "inline-flex w-full touch-manipulation items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors sm:w-auto [@media(pointer:coarse)]:min-h-11 hover:bg-primary/90";
const EMPTY_SECONDARY_ACTION_CLASS =
    "inline-flex w-full touch-manipulation items-center justify-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors sm:w-auto [@media(pointer:coarse)]:min-h-11 hover:bg-background2/60";

function normalizeTag(rawTag: string): string {
    return rawTag.replace(/^#+/, "").trim();
}

/**
 * The SSR list handed over as JSON on the mount container (`DISCOVERY-69`).
 *
 * Never throws: the seed is an optimisation, so a malformed attribute has to cost the round trip it
 * was meant to save and nothing else. Anything that is not an array of objects degrades to `[]`,
 * which is also what an empty hub serializes to, and the caller skips seeding for it.
 */
function parseSsrDiscussions(rawJson: string | undefined): DiscussionWithoutContentDTO[] {
    if (!rawJson) {
        return [];
    }
    try {
        const parsed = JSON.parse(rawJson);
        return Array.isArray(parsed) ? (parsed.filter(Boolean) as DiscussionWithoutContentDTO[]) : [];
    } catch {
        return [];
    }
}

export function TagPage(props: {
    tag: string;
    localizer: Localizer;
    /**
     * Whether this hub has anything to syndicate (`ERRORS-27`). Server-supplied, and false by default
     * so a missing attribute hides the pill rather than advertising an empty feed.
     */
    feedEligible?: boolean;
    /**
     * Whether the topic exists ANYWHERE on Seavien, viewer- and locale-independently (`ERRORS-12`).
     * Server-supplied from the count the controller already computes for its indexability decision;
     * false by default, which is the safe direction — it makes the empty state say less rather than
     * assert a cause it cannot evidence.
     */
    hasGlobalMatches?: boolean;
    /**
     * The language scope the SERVER rendered its list with (`DISCOVERY-39` / `ERRORS-12`, Q12).
     *
     * Server-supplied and defaulting to `"mine"`, which is what this hub has always done, so a missing
     * attribute reproduces today's page exactly. It is the initial value only: the control below owns
     * it from the first render on, through the URL.
     */
    initialLanguageScope?: UserDiscussionLanguageScope;
    /**
     * Round 32 D1/A2 (`DISCOVERY-88`) — the bare `/tag/{tag}` address renders the ALL-languages list,
     * because the viewer's-languages list is empty and the all-languages list is not. Server-decided
     * (`TagController`), false by default so a missing attribute reproduces the narrow hub exactly.
     */
    autoWidened?: boolean;
    /**
     * The canonical path of the authored landing whose slug IS this topic, or `""` when there is none
     * (`DISCOVERY-42`, Q18). Empty for every tag but three, so the empty state renders it as a
     * conditional action.
     */
    landingPath?: string;
    /**
     * Real topics whose names start with the arriving one (`ERRORS-29`, Q18), from the same prefix
     * source the composer's hashtag typeahead uses. Empty for most arrivals.
     */
    nearMissTags?: string[];
    /**
     * The server's own first page of this hub's list, handed to the island so it renders the rows the
     * HTML already carried instead of re-fetching them (`DISCOVERY-69`).
     *
     * Produced by the SAME request that produced the SSR list in `tag.jte`, which is the row's one
     * constraint: a seed from a second query would disagree with the paint it is replacing and would
     * reinvent the reflow rather than remove it. Empty/absent simply falls back to today's fetch, so
     * the two halves of a deploy cannot disagree about what a visitor sees.
     */
    ssrDiscussions?: DiscussionWithoutContentDTO[];
}) {
    const { localizer } = props;
    const tag = React.useMemo(() => normalizeTag(props.tag), [props.tag]);
    const [page, setPage] = React.useState(1);

    /**
     * `DISCOVERY-39` / `ERRORS-12` — the hub's language scope, in the URL.
     *
     * ⚠️ **Q12 REVERSES the wave-12 decision on these two rows**, which chose "re-label only, no
     * language control here". Wave-14 Q12 chose the control the other discovery surfaces already carry.
     * A reader who finds only the older note will read this as a contradiction; it is a supersession.
     *
     * **Why the URL and not `useState`.** `TagController` states the constraint in its own comment: the
     * SSR pass and `SearchPaginationService.browseDiscussionsByTag` must resolve the same locale set, or
     * the page visibly changes content between the server render and hydration — on the product's
     * organic-search doormat. Putting the scope in the address is what lets the server see it, so the
     * first client fetch asks for exactly the list the server already painted. It also makes the scope
     * survive a reload and a shared link, which is how `/search` and `/groups` already behave.
     *
     * Rule 33 holds by construction: `useHistoryBackedState` is the sanctioned wrapper over
     * `NavigationHistoryState`, nothing here writes history itself, and a scope change PUSHES — a
     * member action that changed the address must be undoable with Back. `mine` is the default and is
     * omitted from the URL, so the ordinary tag address does not grow and crawlers keep one canonical
     * form of the page.
     */
    const initialLanguageScope: UserDiscussionLanguageScope = props.initialLanguageScope === "all" ? "all" : "mine";
    /**
     * Round 32 A2 (`DISCOVERY-88`) — what the BARE address renders, which is what the URL is written
     * relative to.
     *
     * On an auto-widened hub the server renders `/tag/{tag}` with every language, so `all` is the
     * address's own state and `mine` is the one that must be spelled out. Writing the URL against a
     * fixed `mine` default would make the hook's mount-time `canonicalize` rewrite the bare address to
     * `?languageScope=all` on arrival (A2: no URL change on arrival), and would drop the member's
     * "Your languages" choice from the address, so a reload would auto-widen over it. The member's
     * choice still PUSHES (rule 33) and Back returns to the widened view.
     */
    const autoWidened = props.autoWidened === true;
    const bareAddressScope: UserDiscussionLanguageScope = autoWidened ? "all" : "mine";
    const { state: languageScope, setState: setLanguageScope } = useHistoryBackedState<UserDiscussionLanguageScope>({
        initialState: initialLanguageScope,
        readState: (location, fallback) => {
            const param = new URLSearchParams(location.search).get("languageScope");
            if (param === "all") {
                return "all";
            }
            if (param === "mine") {
                return "mine";
            }
            // Absent: the bare address. On an auto-widened hub it renders `all` by construction (the
            // server decided it); otherwise the pre-round-32 fallback is kept as it was.
            return autoWidened ? "all" : fallback === "all" ? "all" : "mine";
        },
        buildUrl: (scope, location) => {
            const params = new URLSearchParams(location.search);
            if (scope === bareAddressScope) {
                params.delete("languageScope");
            } else {
                params.set("languageScope", scope);
            }
            const search = params.toString();
            return `${location.pathname}${search ? `?${search}` : ""}${location.hash}`;
        },
        areEqual: (left, right) => left === right,
    });

    /**
     * `DISCOVERY-69` — hand the island the list the server already delivered.
     *
     * `tag.jte` server-renders up to 24 discussions, then this island mounts with `createRoot()`, which
     * discards those children, and `SearchPaginatedList` ran its own `fetchPage` over the wire before it
     * could render anything — painting five skeleton rows where real, already-delivered content had
     * been. That is JS subtracting from the HTML we shipped (rule 32) on the one surface whose entire
     * stated purpose is organic-search entry, and it ran `findDiscussionsByTag` TWICE on every cold
     * arrival. `seedSearchPaginatedListCache` exists for exactly this and only `/search` was using it.
     *
     * Seeded during render, not in an effect, and that is the point: the child's `ensurePageLoaded`
     * effect runs after this render, finds page 1 already satisfied, and never fetches. An effect here
     * would run AFTER the child's and lose the race it exists to win.
     *
     * Keyed to `initialLanguageScope`, never to the live `languageScope`: the seed describes the list
     * the SERVER rendered, so widening the scope must fall through to a real fetch rather than be served
     * the narrowed rows under a widened identity. The ref makes it once per mount, so paging away and
     * back cannot re-seed page 1 over the reader's loaded pages.
     */
    const seededCacheKeyRef = React.useRef<string | null>(null);
    const ssrDiscussions = props.ssrDiscussions;
    const seedCacheKey = `tag:${tag}:${initialLanguageScope}`;
    if (seededCacheKeyRef.current !== seedCacheKey && ssrDiscussions && ssrDiscussions.length > 0) {
        seededCacheKeyRef.current = seedCacheKey;
        seedSearchPaginatedListCache(seedCacheKey, ssrDiscussions);
    }

    React.useEffect(() => {
        setPage(1);
    }, [tag, languageScope]);

    const fetchPage = React.useCallback(async (cursor?: string): Promise<DiscussionWithoutContentDTO[]> => {
        try {
            const response = await ClientFactory.getDiscussionsByTag(tag, cursor, languageScope);
            return response?.data ?? [];
        } catch (error: any) {
            const status = error?.response?.status;
            logApiFailure(`[TagPage] getDiscussionsByTag error status=${status}`, error);
            throw new Error(`Failed to fetch discussions by tag: ${status ?? error?.message ?? error}`);
        }
    }, [tag, languageScope]);

    const heading = `#${tag}`;
    const rssHref = `/tag/${encodeURIComponent(tag)}/rss`;
    /**
     * Whether a LANGUAGE rule is even capable of being what hid this hub's posts (`DISCOVERY-52`,
     * `DISCOVERY-50`). Read at render rather than memoised on purpose: it is two synchronous reads and
     * both of its inputs are stable for the life of the page.
     */
    const languageScopeCouldHideResults = languageScope === "mine" && MetaHandler.isUserLoggedIn();
    const landingPath = props.landingPath ?? "";
    const nearMissTags = React.useMemo(
        () => (props.nearMissTags ?? []).filter((candidate) => candidate && candidate !== tag).slice(0, 4),
        [props.nearMissTags, tag],
    );

    /**
     * `DISCOVERY-85` — the empty hub's "be first here" moment, seeded with the topic.
     *
     * <p>An empty hub had established in three sentences that nobody has written about this subject and
     * then offered three actions, every one of which walks away from it. The only route to authoring on
     * the screen was the unlabelled compose FAB — route-independent app chrome that names neither the
     * topic nor what it does, and that for an anonymous visitor opens the account-access dialog.
     *
     * <p><b>Nothing here is new plumbing.</b> This is the identical handoff `DISCOVERY-19` / `D20`
     * shipped for the failed *search* query, pointed at the hub's own tag: `subject` seeds the TITLE
     * and, since round 38 A15, `text` seeds `#tag` into the body, `buildSharedComposerSeed` drops an over-long subject rather than truncating it mid-word, and
     * `triggerCreateDiscussionEntry` owns the anonymous path — sign in, land in the composer — so the
     * question of whether a signed-out visitor may be invited is answered by that precedent rather than
     * re-opened here.
     *
     * <p>No new analytics event, for the reason `DISCOVERY-19`'s closure gives on the search side: the
     * honest baseline for an offer like this is the surface's existing measurement, not a bespoke
     * counter minted alongside the thing it is supposed to judge.
     */
    const writeAboutTopic = React.useCallback(() => {
        if (!tag) {
            return;
        }
        // The BARE tag, not the `#tag` heading, as the title: a title that opens with a hashtag is a
        // hashtag, not a subject. Round 38 A15 (ERRORS-50): the BODY carries `#tag` as plain text, which
        // is how a post is tagged (inline hashtags, no tag field), so what the member publishes lands on
        // the hub that invited it. Text nodes only, like every seed.
        const seed = buildSharedComposerSeed({ subject: tag, text: `#${tag}` }, Date.now());
        if (seed) {
            writeSharedComposerSeed(seed);
        }
        triggerCreateDiscussionEntry(localizer, MetaHandler.isUserLoggedIn(), "tag-hub-empty-write-it");
    }, [tag, localizer]);

    /**
     * The hub's OWN empty state (`DISCOVERY-5`).
     *
     * `SearchPaginatedList`'s shared one serves `/search`, and this route passes `query=""` — so it fell
     * through to a panel headed "Search results" above a magnifying glass, on a page titled `#tag` that
     * the visitor reached from a link, with no action of any kind. That reads as "you typed something
     * wrong", which is not a move available to someone who arrived by URL.
     *
     * This state is not rare: `TagController` scopes the list to the viewer's reading set, so a
     * non-English visitor can land on a hub that is rich in English and be shown nothing. The copy says
     * that plainly rather than reporting a failed search, and both actions are lateral moves out of a
     * dead end. Both reuse already-translated keys.
     */
    const emptyState = (
        <div
            className="rounded-xl border border-border bg-card px-6 py-8 text-center"
            data-playwright-id="tag-page-empty"
        >
            {/*
              * `DISCOVERY-77` — a border, not a wash.
              *
              * `bg-background2/80` over `bg-card` sampled at 1.015:1 in light and 1.022:1 in dark, so
              * the plate was invisible in both themes and what rendered was a bare `#` floating in the
              * middle of the card — the empty state reading as content that failed to load rather than
              * as a designed state. Contrast was never the issue and this element is decorative and
              * `aria-hidden`, so the 3:1 non-text floor does not bind it; the harm is compositional.
              *
              * A full-opacity `border-border` rather than a different fill, because that is the flat-X
              * elevation idiom this design language already prefers over washes (rule 23), and because
              * a border separates in BOTH themes by construction: the two grounds differ in direction,
              * not only in value, so any fill chosen against one of them has to be re-checked against
              * the other.
              */}
            <div className="mx-auto flex size-14 items-center justify-center rounded-full border border-border text-muted-foreground">
                <Hash className="size-6" aria-hidden="true" />
            </div>
            {/*
              * `DISCOVERY-62` — the HEADING branches on the same flag the description below already
              * branches on, so the two lines make ONE claim.
              *
              * It used to render `tag.page.empty.title` ("No posts on this topic yet")
              * UNCONDITIONALLY, above a description that, on the `hasGlobalMatches` branch, says
              * "Nothing has been posted under #philosophy in your languages yet". So the largest,
              * boldest, highest-contrast line on the page asserted a GLOBAL absence that the smaller
              * grey line under it immediately contradicted — and the heading is the line a scanner
              * reads and the line a screen reader reaches first inside this card. A visitor arriving
              * from a search for philosophy was told, in the loudest voice on the page, that Seavien
              * has nothing on the subject when in fact it does and only a scope is hiding it.
              *
              * `ERRORS-12` branched the description and left the heading asserting the stronger claim
              * in both branches; this is that residue. The condition is the IDENTICAL expression the
              * description uses, not a restatement of it, so the two cannot drift apart again.
              */}
            <h2 className="mt-4 text-lg font-semibold text-foreground">
                {localizer(props.hasGlobalMatches && languageScopeCouldHideResults
                    ? "tag.page.empty.title.language_scoped"
                    : "tag.page.empty.title")}
            </h2>
            {/*
              * `ERRORS-12` — WHICH empty is this?
              *
              * `tag.page.empty.description` reads "Nothing has been posted under #x in your languages
              * yet", and that sentence asserts a cause. `DISCOVERY-5` minted it deliberately, and for
              * the case it was looking at — a real topic, empty for this reader — it is right and is
              * the part a visitor could never have guessed. What it never distinguished is the case
              * where the topic has never existed at all, which is most of what reaches `/tag/*`. There
              * the sentence is simply false: no language setting would reveal anything, and a member
              * who believes it goes hunting through settings for content that is not there.
              *
              * The server can tell them apart on this same request and now says so:
              * `countIndexableDiscussionsByTag` is deliberately global and viewer-independent (it
              * exists to decide indexability), so it is evidence for "something exists" and not for a
              * number — which is why nothing derived from it reaches the copy as a figure.
              *
              * No key is minted. The neutral branch reuses `search.no_results.description_everywhere`
              * ("Nothing on Seavien matches that yet. Try different words."), present in all 25
              * catalogs, which states the outcome and blames nothing.
              */}
            {/*
              * `DISCOVERY-52` — the language claim is now made ONLY when the request can evidence it.
              *
              * `tag.page.empty.description` says "Nothing has been posted under #x IN YOUR LANGUAGES
              * yet", and until this row the page asserted that whenever `hasGlobalMatches` was true.
              * Two things were wrong with that, both code-verified and neither of them a wording
              * problem — which is why this is a mechanism fix and the string is untouched:
              *
              * 1. THE VISITOR MAY HAVE NO LANGUAGES. `TagController` resolves the locale set through
              *    `getLoggedInUserIdOr0IfNotLoggedIn()`, and `ReadingLanguageService` passes `List.of()`
              *    for `viewerId <= 0` — an anonymous reader's set is derived from the request locale
              *    plus the English bridge. So "your languages", and the pill's "Your languages",
              *    attributed to a stranger a choice they never made and CANNOT INSPECT: there is no
              *    language settings page for them to go check. `ERRORS-12` was minted against exactly
              *    this harm ("a member who believes it goes hunting through settings for content that
              *    is not there") and for an anonymous reader it is worse, because there is no hunt
              *    available. `/tag/*` is the product's organic-search doormat, so this is the majority
              *    case, not the edge.
              *
              * 2. THE SCOPE MAY ALREADY BE WIDE. When the control below is set to "All languages" the
              *    list is not language-scoped at all, so no language rule can be what hid anything.
              *
              * What is NOT claimed to be fixed here: `hasGlobalMatches` comes from
              * `countIndexableDiscussionsByTag` and the list comes from `findDiscussionsByTag`, and the
              * two disagree on several predicates (`search_indexable`, a minimum body length, an author
              * recommendability flag, `hideSensitive`, a `created_at` cutoff, DEMOTED rows). Several of
              * those produce `count > 0` with `list = 0` for reasons language had nothing to do with,
              * so even a logged-in member on the narrow scope gets an assertion the request did not
              * test. EARNING it means re-running the list query once with the widened locale set and
              * claiming language only when that second run is non-empty — a server-side decision about
              * paying for an extra query on the SEO doormat, which the row parks as a product call.
              * This narrows the false claim to the case where it is at least possible; it does not
              * establish it.
              *
              * The fallback is the neutral branch this state already had, and no key is minted.
              */}
            <p
                className="mt-2 text-sm leading-6 text-muted-foreground"
                data-playwright-id="tag-page-empty-description"
            >
                {props.hasGlobalMatches && languageScopeCouldHideResults
                    ? localizer("tag.page.empty.description", heading)
                    : localizer("search.no_results.description_everywhere")}
            </p>
            {/*
              * `DISCOVERY-35` / `ERRORS-17` — the coarse-pointer floor, on both anchors.
              *
              * Measured at 36.2 and 38.5 CSS px on a 412px frame, against the 44px floor
              * `css/03-tap-targets.css` calls the canonical rule. On the empty state these two are
              * literally the only way forward that is not the browser Back button, on a page whose
              * likely visitor arrived from organic search on a phone.
              *
              * `min-h-11` rather than `touch-hit-44`: in `es` the pair STACKS at `gap-3`, so growing
              * the box makes the pitch 44 + 12 = 56 by construction, whereas a centred 44px slug on a
              * 36px anchor overhangs 4px each side and would collide across a 12px gap.
              *
              * Gated on `(pointer: coarse)`, never on `sm:` — the floor is a property of the input
              * device, and `scripts/check-tap-target-mechanisms.mjs` check C fails a breakpoint form.
              *
              * ⚠️ `ERRORS-17` claimed routing these through the shared `Button` would inherit the
              * floor "for free". It would not: `buttonVariants` gives the coarse floor to
              * `size="icon-touch"` ONLY. That is why the RSS pill below, already on the shared
              * `Button` since `DISCOVERY-16`, was still 32px and is floored here too.
              */}
            {/*
              * `DISCOVERY-44` — the row DECIDES what it does when it stacks, instead of letting
              * `flex-wrap` decide.
              *
              * At 412 CSS px in `es` the pair wrapped: "Buscar" sat alone and centred at 80 px while
              * "Explorar todas las publicaciones" dropped onto its own row at 251 px, filling 84% of
              * the card's content measure. The outline secondary ended up 3.1x the width of the
              * filled primary directly above it, so the largest and most obviously tappable target
              * was the action the design demoted — and on a phone width IS the hierarchy.
              *
              * Below `sm` the pair is a deliberate stacked column of equal-width buttons with the
              * primary leading; at `sm` and above it is the centred row it always was, where both
              * labels fit on one line in every locale. No label was shortened, so no catalog moves.
              *
              * The `[@media(pointer:coarse)]:min-h-11` floor stays on both (`DISCOVERY-35` /
              * `ERRORS-17`) and is now in exactly the shape the tap-target rule prefers: a floor on a
              * full-width row, never a centred 44px slug inside one.
              */}
            <div className="mt-5 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
                {/*
                  * Round 32 A3 (`DISCOVERY-91`, answering `DISCOVERY-50`'s parked question) — ONE lead.
                  *
                  * Five closed rows each added a control to this card and nobody judged them together;
                  * at 412 px the stack took two thirds of the first screen, four of five exits repeated
                  * something already on screen, and the one unique destination sat fourth in outline.
                  * Decided 2026-09-24 (`designs/01-now/2026-09-24-board-settle-round-32-work-order.md`):
                  *
                  * - The single filled primary is "Explore #topic" (the authored landing) when one
                  *   exists, else "Search for #topic". With a landing, search stays as the outline
                  *   secondary: the card never carries two filled controls.
                  * - "All languages" is GONE. `DISCOVERY-50`'s parked question was "should the widened
                  *   view simply be the default on an empty hub?", and D1 answered yes: `TagController`
                  *   renders the all-languages list whenever it has rows, so on the bare address this
                  *   card appears only when the widened list is empty too and the action would be a
                  *   no-op. Where widening still means something (after a member explicitly chose "Your
                  *   languages"), the scope pill above the card does it with the same words.
                  * - "Browse all posts" is GONE: the bottom nav's Home and the crawl nav's Posts carry it.
                  * - The "Write about #topic" invitation below the rule stays (`DISCOVERY-85`): it is a
                  *   creation nudge, not an exit.
                  *
                  * `DISCOVERY-44`'s stacking contract is unchanged: a deliberate `flex-col` of equal-width
                  * pills below `sm`, a centred row above it.
                  */}
                {/*
                  * `DISCOVERY-42` (Q18) — the product owns an authored page about exactly this topic,
                  * and the hub never mentioned it, least of all when it has nothing else to show.
                  *
                  * A CONDITIONAL third action, not a fixture: `LandingTopics` registers three demand
                  * landings whose slugs are literally tag names (`/philosophy`, `/faith`,
                  * `/politics`), and the reverse lookup runs over the registry precisely so it cannot
                  * go stale the day a fourth ships. Every other tag renders nothing here.
                  *
                  * `DISCOVERY-63` — the label now says it is a DIFFERENT PAGE, and that reverses
                  * `DISCOVERY-42`'s recorded zero-copy choice on new evidence.
                  *
                  * The label used to be the bare topic string, byte-identical to this page's own `h1`.
                  * On a phone `DISCOVERY-44` makes this row a `flex-col` of equal-width pills, so a
                  * reader one sentence into being told there is nothing here saw three stacked controls
                  * of identical width and weight, the third labelled with the title of the page they
                  * were already on — distinguished from it only by a 16 px compass glyph carrying
                  * `aria-hidden`. The likeliest reading is that the button does nothing, so the one
                  * recovery built for this exact moment was the action least likely to be pressed.
                  * `DISCOVERY-42`'s closure recorded "No copy: the label is the topic itself" as a
                  * deliberate trade; `DISCOVERY-63` is the first phone capture of what it bought.
                  *
                  * `tag.page.explore-topic` ("Explore {0}") is a new key, minted with a VERB and built
                  * to sit beside its sibling `tag.page.search-topic` ("Search for {0}") — the same
                  * shape, so the two exits read as two destinations rather than as one label and one
                  * echo. The topic is still in the label, which is what that trade was protecting.
                  *
                  * The layout question the row raised is already answered. `DISCOVERY-44` made this
                  * row a deliberate `flex-col` of equal-width buttons below `sm` and the centred
                  * `flex-row` above it, so a third action joins a stack that decides what it does when
                  * it stacks — it is not a third item handed to `flex-wrap`, which is what produced
                  * the Spanish hierarchy inversion the row measured.
                  */}
                {landingPath ? (
                    <a
                        href={landingPath}
                        className={EMPTY_PRIMARY_ACTION_CLASS}
                        data-playwright-id="tag-page-empty-landing"
                    >
                        <Compass className="size-4" aria-hidden="true" />
                        <span>{localizer("tag.page.explore-topic", heading)}</span>
                    </a>
                ) : null}
                <a
                    href={`/search?searchQuery=${encodeURIComponent(tag)}`}
                    className={landingPath ? EMPTY_SECONDARY_ACTION_CLASS : EMPTY_PRIMARY_ACTION_CLASS}
                    data-playwright-id="tag-page-empty-search"
                >
                    {/*
                      * `ERRORS-28` — the label names the TOPIC it will search for.
                      *
                      * It used to be `header.tooltip.search`, the hover label for the header's
                      * magnifier icon, on an anchor that goes to `/search?searchQuery=<tag>`. A member
                      * reading only the two button labels saw one that described a destination
                      * ("Browse all posts") and one bare verb that read as "open the search box", so
                      * the button that actually does something with the topic they came for looked
                      * like the weaker of the two and its best feature — the query is already filled
                      * in — was invisible.
                      *
                      * ⚠️ **This reverses `DISCOVERY-5`'s recorded reuse decision**, deliberately and
                      * on the record (wave-12 sheet Q6, option A). That closure chose two
                      * already-translated keys so the 25-catalog obligation was met without minting
                      * near-duplicates; this row names the user cost that bought, and the operator
                      * chose the label. Read `DISCOVERY-5`'s closure before changing it back.
                      */}
                    {localizer("tag.page.search-topic", heading)}
                </a>
            </div>
            {/*
              * `ERRORS-29` (Q18) — a mistyped or truncated tag gets the topics it is one keystroke
              * from, instead of two exits that both walk away from the subject.
              *
              * The rows reuse the hub's own link vocabulary rather than becoming a third and fourth
              * button: they are lateral topic moves, not the page's actions, and putting them in the
              * action row would make the demoted exits compete with them at phone width.
              *
              * ⚠️ Honest limit, recorded rather than hidden: the source is a PREFIX match on the whole
              * arriving string (`TagService.findTagSuggestions` → `tagRepository.findByNamePrefix`,
              * the same one the composer's hashtag typeahead asks), so `/tag/philo` finds
              * `philosophy` and `/tag/phillosophy` finds nothing. Q18 named that source and called the
              * move deliberately narrow; an edit-distance search is a different, unasked question.
              */}
            {nearMissTags.length > 0 ? (
                <div
                    className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1"
                    data-playwright-id="tag-page-empty-near-misses"
                >
                    {nearMissTags.map((nearMiss) => (
                        <a
                            key={nearMiss}
                            href={`/tag/${encodeURIComponent(nearMiss)}`}
                            className="inline-flex items-center touch-manipulation text-sm font-medium text-primary underline-offset-4 [@media(pointer:coarse)]:min-h-11 hover:underline"
                            data-playwright-id="tag-page-empty-near-miss"
                        >
                            #{nearMiss}
                        </a>
                    ))}
                </div>
            ) : null}
            {/*
              * `DISCOVERY-85` — the invitation to be the first, LAST and subordinate.
              *
              * Last because the two blocks above it are corrections: the action row offers the ways off
              * a dead end, and the near-miss row offers the topic the visitor probably meant. Only once
              * both have been declined does "you meant this topic and it is empty" hold, and that is the
              * moment this sentence is true.
              *
              * Subordinate by weight and by variant, both deliberate. The card already carries exactly
              * one filled primary ("Search for #topic") and the row's own measurement is that the screen
              * has a competing-emphasis problem — the compose FAB and that CTA are both filled primary
              * and the louder one leads to a login wall. A second filled control here would make that
              * worse, and the same operator answer that put this invitation on the page also demoted
              * write-a-post to secondary on `/search` (wave-19 re-adjudication §1.1). ⛔ The FAB itself
              * is untouched; the row explicitly rules that out.
              *
              * No key minted. `search.no_results.write_it.description` / `.action` ship in all 25
              * catalogs and, since `DISCOVERY-61` deleted their `.title` sibling, had exactly one
              * consumer; the action interpolates the hub's own `#topic`, so the control names the
              * subject rather than opening a generic composer — which is the thing `DISCOVERY-19`
              * criticised in the first place.
              */}
            <div className="mt-6 border-t border-border pt-5" data-playwright-id="tag-page-empty-write-it">
                <p className="text-sm font-medium leading-6 text-foreground">
                    {localizer("search.no_results.write_it.description")}
                </p>
                <div className="mt-3 flex justify-center">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={writeAboutTopic}
                        // `max-w-full`: the label interpolates an unbounded tag name and must not grow
                        // the control past the card on a phone. The coarse-pointer floor matches every
                        // other control in this card (`ERRORS-17` — being on the shared `Button` does
                        // NOT inherit it).
                        className="max-w-full [@media(pointer:coarse)]:min-h-11"
                        data-playwright-id="tag-page-empty-write-about-topic"
                    >
                        <PenLine className="mr-2 size-4 shrink-0" aria-hidden="true" />
                        <span className="truncate">
                            {localizer("search.no_results.write_it.action", heading)}
                        </span>
                    </Button>
                </div>
            </div>
        </div>
    );

    return (
        // `ERRORS-42` — no `min-h-screen` here. This wrapper sits BENEATH the 64px app header and
        // INSIDE its own `py-4`, so the three heights add rather than share: 64 + 1200 + 32 = 1296
        // against a 1200px desktop viewport, 61 + 915 + 32 = 1008 against 915 on a phone. Every
        // `/tag/*` page therefore always scrolled, and on a short one the scrolled region held
        // nothing — which on an empty hub reads as content still loading rather than as a short page.
        // The wrapper paints no background and no border, so the class could never have been filling
        // anything; the viewport floor is owned one level up by `basic_layout.jte`'s `min-h-svh`.
        // `DISCOVERY-78` — ONE owner for the page gutter, and it is the layout. `basic_layout.jte`'s
        // `mainClass` already applies `px-4 sm:px-4 lg:px-6` to every page's main element; this wrapper
        // added a second `px-4 sm:px-6` inside it, so at 412 px each side spent 32 CSS px on gutters
        // against the sibling `/groups` directory's 16, and moving between two discovery surfaces that
        // share the same tab strip shifted the content column by 32 px. `py-4` stays: vertical rhythm
        // is this page's, horizontal rhythm is the layout's.
        //
        // ⚠️ `tag/tag.jte` MUST match this class for class (`DISCOVERY-18`) or the pre-mount paint and
        // the island disagree on the column width on the product's organic-search doormat.
        <div className="w-full py-4">
            <div className="mx-auto w-full max-w-3xl space-y-5">
                <header className="space-y-2 border-b border-border/60 pb-4">
                    <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                        {/* `ERRORS-30` — a tag is `^[a-z0-9_]*[a-z][a-z0-9_]*$` up to 200 characters,
                            so a long one is a single unbreakable token to the line-breaker (browsers
                            do not break at an underscore under the default `overflow-wrap: normal`).
                            The row's `flex-wrap` rescues the RSS pill by dropping it to a second
                            line; nothing rescued the heading. `min-w-0` on the parent is required
                            as well as the wrap utility: a flex item's default `min-width: auto`
                            refuses to shrink below its content, so the heading would never reach a
                            width at which wrapping applies. Both are no-ops for a tag that fits. */}
                        <h1 className="text-2xl font-semibold wrap-anywhere text-foreground">{heading}</h1>
                        {/*
                          * `DISCOVERY-16` — this is the single interactive element in the page body,
                          * and it used to hand-roll a weaker copy of the shared outline button:
                          * `border-border/60` and no fill. The `/60` was what took its boundary to
                          * 1.22:1 in light and 1.38:1 in dark, against the 3:1 SC 1.4.11 requires for
                          * a control's visual boundary; even after the 2026-08-17 `--line-color` raise
                          * the diluted border only reached ~2.02:1, because dilution, not the token,
                          * was the problem. A floating word rather than a control, on a page whose
                          * only other affordances are header icons.
                          *
                          * Routed through the shared variant rather than re-tuned in place: `outline`
                          * carries full-opacity `border-border` AND the `bg-card` fill that lifts a
                          * control off the page ground, plus the standard focus ring. The design
                          * language elevates with borders rather than shadows, so fill and opacity is
                          * the whole fix. `asChild` keeps it a real anchor, which matters because the
                          * href is the feature.
                          */}
                        {/*
                          * DISCOVERY-17: the label says what is on the other side.
                          *
                          * The pill used to borrow `newsletter.subscribe` ("Subscribe"), and the href
                          * is `/tag/{tag}/rss`, served as application/rss+xml — so a general reader
                          * clicking the one action on the product's SEO entry surface got a wall of
                          * raw XML or a file download: not a subscription, not undoable from this
                          * page, and nothing in the word warned them. Only a 16px feed glyph carried
                          * the hint. Naming the artifact is the whole fix; whether the hub should
                          * instead offer an in-product "follow this topic" is a separate product
                          * question about whether topic-following exists as a feature at all.
                          */}
                        {/*
                          * `ERRORS-27` — offered only when there is something to syndicate.
                          *
                          * On an empty hub this pill is the only control above the fold on desktop,
                          * and it handed over a well-formed feed containing ZERO items, titled and
                          * described for a string nobody has ever posted under: a subscription
                          * offered as the headline action at the exact moment the page says there is
                          * nothing here. On a phone, following it leaves the app shell for raw XML or
                          * a download with no in-product way back.
                          *
                          * The product already applies the opposite rule one surface over —
                          * `SeriesController` advertises a series feed only for series whose `/rss`
                          * endpoint serves content — so this is that mechanism ported, not a new one.
                          * The gate itself is computed server-side and the SSR pass applies it too;
                          * see `TagController` for why the page's own list settles the question
                          * without a second query.
                          *
                          * Whether an empty hub should instead offer an in-product "follow this
                          * topic" is a separate product question about a feature that does not exist
                          * (`DISCOVERY-17`'s closure declined it as a new proposal).
                          */}
                        {props.feedEligible ? (
                        <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="touch-manipulation [@media(pointer:coarse)]:min-h-11"
                        >
                            <a href={rssHref} data-playwright-id="tag-page-rss-subscribe">
                                <Rss className="size-4" aria-hidden="true" />
                                <span>{localizer("tag.page.rss")}</span>
                            </a>
                        </Button>
                        ) : null}
                    </div>
                    {/* `ERRORS-26` (D13) — this line renders UNCONDITIONALLY, and that is the fix
                        rather than an oversight.

                        It used to say "Browse the latest posts tagged #x" about 168 CSS px above a
                        panel saying "No posts on this topic yet", so the header promised a list and
                        the page denied it in two consecutive sentences. The tempting repair is to
                        hide the line once the list resolves to nothing, and it was refused twice: the
                        resolved-empty signal lives inside `SearchPaginatedList`, a shared primitive
                        that also serves `/search`, and `tag/tag.jte`'s crawlable first pass renders
                        no intro at all, so hiding it client-side would widen the pre/post-mount gap
                        `DISCOVERY-18` is about on the page whose whole job is organic search.

                        The string was restated instead, so it is true of an empty hub and a full one:
                        it now describes what this address collects rather than instructing the reader
                        to browse it. One key, 25 catalogs, no signal to lift and no branch to keep in
                        step across two rendering passes. */}
                    <p
                        className="text-sm leading-6 text-muted-foreground"
                        data-playwright-id="tag-page-intro"
                    >
                        {localizer("tag.page.intro", heading)}
                    </p>
                </header>

                {/*
                  * `DISCOVERY-39` / `ERRORS-12` (Q12) — the control the empty state's copy has been
                  * blaming since `DISCOVERY-5` minted it.
                  *
                  * `DiscussionLanguageScopeSelect` reused verbatim: it is the two-option all/mine
                  * toggle for a DISCUSSION list, its accessible name is already
                  * `profile.language-scope.aria` ("Filter posts by language"), and it renders bare so a
                  * caller can place it. Zero new copy, which is what Q12 asked for.
                  *
                  * A row of its own, NOT the header row beside the RSS pill. At 412px the header is
                  * already `h1` + pill under `flex-wrap`; a third child there wraps in the island and
                  * not in the server pass, which is precisely the pre/post-mount reflow `DISCOVERY-18`
                  * fixed on this page. `tag/tag.jte` reserves this row's height and renders no control
                  * — it cannot render one, because `profile.language-scope.*` lives in the frontend
                  * catalog only. Keep the two heights in step.
                  */}
                {/*
                  * `DISCOVERY-76` — the row sizes to its control on a coarse pointer instead of pinning
                  * it at 32 px.
                  *
                  * `SimpleSelect` renders 36 px on a fine pointer and 44 px on a coarse one
                  * (`[@media(pointer:coarse)]:min-h-11`), so a fixed `h-8` box was overhung by 6 px top
                  * and bottom at 412 px: the page's `space-y-5` resolved to ~14 CSS px of visible
                  * clearance above and below the pill while every other gap on the page kept its 20,
                  * and the page's only filter read as belonging to neither the rule above it nor the
                  * card below. `min-h-8` keeps the 32 px reservation as a FLOOR — nothing shifts on
                  * desktop — and the coarse floor states the height the control actually occupies.
                  *
                  * ⚠️ `tag/tag.jte` reserves this row's height and MUST carry the identical pair. The
                  * two files' comments have always stated that invariant; until this row they agreed
                  * with each other and disagreed with the control.
                  */}
                <div
                    className="flex min-h-8 items-center justify-end [@media(pointer:coarse)]:min-h-11"
                    data-playwright-id="tag-page-language-scope"
                >
                    <DiscussionLanguageScopeSelect
                        value={languageScope}
                        onChange={setLanguageScope}
                        localizer={localizer}
                    />
                </div>

                {/*
                  * Round 32 D2 (`DISCOVERY-88`) — the ONE quiet line above an auto-widened list.
                  *
                  * The server rendered every language because nothing was posted in the viewer's own
                  * (`TagController`), so the reader is told why the posts are in other languages, and the
                  * pill above already reads "All languages" — the same control switches back. Only while
                  * the widened view is the one the SERVER chose: a member who picked "All languages"
                  * themselves needs no explanation.
                  *
                  * ⚠️ `tag/tag.jte` RESERVES this line in the same state (same element, same classes,
                  * empty, `aria-hidden`), because the key lives in the frontend catalog only. Keep the
                  * classes and the condition in step with it.
                  */}
                {autoWidened && languageScope === "all" ? (
                    <p
                        className="text-sm leading-6 text-muted-foreground"
                        data-playwright-id="tag-page-auto-widened-note"
                    >
                        {localizer("tag.page.empty.title.language_scoped")}
                    </p>
                ) : null}

                <SearchPaginatedList<DiscussionWithoutContentDTO>
                    // The scope is part of the identity of the list, not a filter over one: a widened
                    // page must not be served the narrowed page's cached rows on the way back. It is
                    // also the key the SSR seed above writes (`DISCOVERY-69`), which is why the seed is
                    // keyed to the scope the SERVER rendered rather than to this live one.
                    cacheKey={`tag:${tag}:${languageScope}`}
                    query=""
                    page={page}
                    localizer={localizer}
                    resultLabel={localizer("search.tab.discussions")}
                    fetchPage={fetchPage}
                    getId={extractSearchResultId}
                    renderItem={(item) => (
                        <SearchDiscussionResultRow
                            key={extractSearchResultId(item)}
                            discussion={item}
                            localizer={localizer}
                        />
                    )}
                    onPageChange={setPage}
                    emptyState={emptyState}
                    // A /tag/* hub exists ONLY for SEO: tag.jte server-renders the <h1> and a real
                    // anchor list, and this island mounts over it. Without this the topic hub a
                    // crawler renders on a failed fetch is a retry button (frontend F2, 2026-08-04).
                    ssrFallback={crawlableDiscussionListFallback("TagPage")}
                />
            </div>
        </div>
    );
}

export const mountTagPage = createReactMount({
    name: "mountTagPage",
    getProps: ({ dataset, localizer }) => ({
        tag: dataset.tag ?? "",
        // Both default to FALSE when the attribute is absent, which makes the two deploy halves
        // order-independent and points the failure at "says less" rather than "asserts wrongly"
        // (AGENTS.md rule 34's own reasoning, applied to a page rather than a route).
        feedEligible: dataset.feedEligible === "true",
        hasGlobalMatches: dataset.hasGlobalMatches === "true",
        // Same reasoning, one step further: an absent scope means "mine", the list this page has
        // always rendered, so the two halves of a deploy cannot disagree about what a member sees.
        initialLanguageScope: (dataset.languageScope === "all" ? "all" : "mine") as UserDiscussionLanguageScope,
        // Round 32 A2: false when absent, which reproduces the narrow hub and its URL rules exactly.
        autoWidened: dataset.autoWidened === "true",
        landingPath: dataset.landingPath ?? "",
        nearMissTags: (dataset.nearMissTags ?? "").split(",").map((entry) => entry.trim()).filter(Boolean),
        // `DISCOVERY-69` — the server's own first page, so the island renders what the HTML already
        // carried. Parsed defensively and degrading to `[]`: a malformed or missing attribute must cost
        // one fetch, never the page. `[]` is also what a hub with no posts serializes to, and the seed
        // is skipped for it, so the empty state is reached exactly as before.
        ssrDiscussions: parseSsrDiscussions(dataset.ssrDiscussions),
        localizer,
    }),
    render: (props) => <TagPage {...props} />,
});

export default TagPage;
